import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "react-hot-toast";
import authService from "../../services/auth.service";
import { updatePassword, signInWithEmailAndPassword } from "firebase/auth"; // We might need to sign in first if not already
import { auth } from "../../firebase.config";

// --- Schema Definitions ---
const passwordSchema = z
  .object({
    password: z.string().min(6, "Password must be at least 6 characters"),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

const otpSchema = z.object({
  otp: z.string().length(6, "OTP must be 6 digits"),
});

export default function SetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const emailParam = searchParams.get("email");

  const [step, setStep] = useState(1); // 1: Password Input, 2: OTP Verify
  const [loading, setLoading] = useState(false);
  const [tempPassword, setTempPassword] = useState(null);

  // Forms
  const {
    register: registerPwd,
    handleSubmit: handleSubmitPwd,
    reset: resetPwd,
    formState: { errors: errorsPwd },
  } = useForm({
    resolver: zodResolver(passwordSchema),
  });

  const {
    register: registerOtp,
    handleSubmit: handleSubmitOtp,
    reset: resetOtp,
    formState: { errors: errorsOtp },
  } = useForm({
    resolver: zodResolver(otpSchema),
  });

  useEffect(() => {
    if (!emailParam) {
        // If no email in param, maybe they are logged in?
        if (!auth.currentUser) {
             toast.error("Invalid link. Please contact support.");
             navigate("/login");
        }
    }
  }, [emailParam, navigate]);


  // Step 1: Submit Password -> Send OTP
  const onPasswordSubmit = async (data) => {
    setLoading(true);
    try {
      // 1. Save password in state to use later
      setTempPassword(data.password);

      // 2. Trigger OTP Send
      await authService.sendOtp(emailParam || auth.currentUser?.email);
      
      toast.success(`Verification code sent to ${emailParam || auth.currentUser?.email}`);
      setStep(2);
    } catch (error) {
      console.error("Error sending OTP:", error);
      toast.error(error.message || "Failed to send verification code");
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Submit OTP -> Verify -> Update Password
  const onOtpSubmit = async (data) => {
    setLoading(true);
    const targetEmail = emailParam || auth.currentUser?.email;

    try {
      // 1. Verify OTP AND Update Password on Backend
      // We pass tempPassword so the backend can use Admin SDK to update it.
      await authService.verifyOtp(targetEmail, data.otp, tempPassword);
      
      // If success, it means backend updated the password.
      // Since we used Admin SDK, the user session on THIS device is not automatically updated if they were logged in anonymously (rare here).
      // Or if they were not logged in, they are still not logged in.
      
      toast.success("Password set successfully! Please login with your new password.");
      navigate("/login");

    } catch (error) {
      console.error("Error verifying OTP:", error);
      toast.error(error.message || "Invalid code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center px-6 py-24">
      <div className="w-full max-w-lg bg-white border border-black rounded-xl shadow-2xl p-8">
        <h1 className="text-2xl font-bold text-center text-gray-800 mb-6">
          {step === 1 ? "Set Your Password" : "Verify Email"}
        </h1>

        {/* STEP 1: PASSWORD INPUT */}
        {step === 1 && (
          <form onSubmit={handleSubmitPwd(onPasswordSubmit)} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Password
              </label>
              <input
                type="password"
                {...registerPwd("password")}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-black focus:outline-none"
                placeholder="••••••••"
              />
              {errorsPwd.password && (
                <p className="text-red-500 text-xs mt-1">{errorsPwd.password.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password
              </label>
              <input
                type="password"
                {...registerPwd("confirmPassword")}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-black focus:outline-none"
                placeholder="••••••••"
              />
              {errorsPwd.confirmPassword && (
                <p className="text-red-500 text-xs mt-1">
                  {errorsPwd.confirmPassword.message}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-black text-white py-2 rounded-lg font-semibold hover:opacity-90 disabled:opacity-70 transition"
            >
              {loading ? "Processing..." : "Continue"}
            </button>
          </form>
        )}

        {/* STEP 2: OTP VERIFICATION */}
        {step === 2 && (
          <form onSubmit={handleSubmitOtp(onOtpSubmit)} className="space-y-4">
             <div className="text-center text-sm text-gray-500 mb-4">
               We sent a 6-digit code to <strong>{emailParam || auth.currentUser?.email}</strong>
             </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Verification Code
              </label>
              <input
                type="text"
                maxLength={6}
                {...registerOtp("otp")}
                className="w-full px-4 py-2 border rounded-lg text-center tracking-widest text-lg focus:ring-2 focus:ring-black focus:outline-none"
                placeholder="000000"
              />
              {errorsOtp.otp && (
                <p className="text-red-500 text-xs mt-1">{errorsOtp.otp.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-black text-white py-2 rounded-lg font-semibold hover:opacity-90 disabled:opacity-70 transition"
            >
              {loading ? "Verifying..." : "Verify & Save"}
            </button>
            
            <button
                type="button"
                className="w-full text-gray-500 text-sm mt-2 hover:underline"
                onClick={() => {
                  resetPwd();
                  resetOtp();
                  setTempPassword(null);
                  setStep(1);
                }}
            >
                Start Over
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
